import { Component, OnInit, OnDestroy, ViewChild, HostListener, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LeagueService, League } from '../../services/league';
import { SocketService, ConnectedUser, DraftHistoryEntry, TeamInfo, Player } from '../../services/socket';
import { AuthService } from '../../services/auth';
import { Chat, SystemMessage } from './components/chat/chat';
import { CheatSheet } from './components/cheat-sheet/cheat-sheet';
import { DraftStage } from './components/draft-stage/draft-stage';
import { TheTape } from './components/the-tape/the-tape';
import { PlayerCard, PlayerCardData } from '../../components/player-card/player-card';
import { Modal } from '../../components/modal/modal';

@Component({
  selector: 'app-draft',
  imports: [CommonModule, Chat, CheatSheet, DraftStage, TheTape, PlayerCard, Modal],
  templateUrl: './draft.html',
  styleUrl: './draft.css',
})
export class Draft implements OnInit, OnDestroy {
  @ViewChild(DraftStage) draftStage!: DraftStage;

  league = signal<League | null>(null);
  isLoading = signal(true);
  error = signal<string | null>(null);
  systemMessages = signal<SystemMessage[]>([]);
  isChatDrawerOpen = signal(false);
  isCheatSheetOpen = signal(false);
  isTapeCollapsed = signal(true);
  isCommissioner = signal(false);
  unreadChatCount = signal(0);
  watchlistPlayerIds = signal<Set<number>>(new Set());
  allPlayers = signal<any[]>([]);
  selectedHandCard = signal<DraftHistoryEntry | null>(null);
  isHandCardModalOpen = computed(() => this.selectedHandCard() !== null);

  watchlistPlayers = computed(() => {
    const ids = this.watchlistPlayerIds();
    return this.allPlayers().filter(p => ids.has(p.id));
  });

  userTeamHistory = computed<DraftHistoryEntry[]>(() => {
    const settings = this.socketService.draftSettings();
    const currentUser = this.authService.currentUser();
    if (!settings?.teams || !currentUser) return [];
    const userTeam = settings.teams.find(t => t.user_id === currentUser.id);
    if (!userTeam) return [];
    return (settings.draftHistory || []).filter(e => e.team_id === userTeam.team_id);
  });

  otherTeams = computed<TeamInfo[]>(() => {
    const settings = this.socketService.draftSettings();
    const currentUser = this.authService.currentUser();
    if (!settings?.teams) return [];
    return settings.teams.filter(t => t.user_id !== currentUser?.id);
  });

  draftRoundInfo = computed(() => {
    const settings = this.socketService.draftSettings();
    if (!settings || settings.status === 'not_started') return 'DRAFT · NOT STARTED';
    const total = settings.draftHistory?.length ?? 0;
    return `PICK ${total + 1}`;
  });

  private lastSeenChatCount = 0;

  private chatCountEffect = effect(() => {
    const count = this.socketService.chatMessages().length;
    if (count > this.lastSeenChatCount && !this.isChatDrawerOpen()) {
      this.unreadChatCount.update(n => n + (count - this.lastSeenChatCount));
    }
    this.lastSeenChatCount = count;
  });

  private leagueHash: string | null = null;
  private subscriptions: Subscription[] = [];

  // Computed signal for user's budget and max bid
  userBudget = computed(() => {
    const settings = this.socketService.draftSettings();
    const currentUser = this.authService.currentUser();
    const league = this.league();

    if (!settings?.teams || !currentUser || !league) return null;

    // Find user's team
    const userTeam = settings.teams.find(t => t.user_id === currentUser.id);
    if (!userTeam) return null;

    // Calculate spent and players drafted from draft history
    const userDraftHistory = (settings.draftHistory || []).filter(
      entry => entry.team_id === userTeam.team_id
    );
    const spent = userDraftHistory.reduce((sum, entry) => sum + entry.cost, 0);
    const playersDrafted = userDraftHistory.length;

    const remaining = league.team_budget - spent;
    const rosterSpotsNeeded = league.roster_size - playersDrafted;

    // Max bid = remaining - (roster spots still needed - 1)
    // This ensures you have at least $1 for each remaining roster slot
    const maxBid = rosterSpotsNeeded > 1 ? remaining - (rosterSpotsNeeded - 1) : remaining;

    return {
      total: league.team_budget,
      spent,
      remaining,
      maxBid: Math.max(1, maxBid),
      playersDrafted,
      rosterSize: league.roster_size
    };
  });

  // Computed status color based on auction state
  statusColor = computed(() => {
    const settings = this.socketService.draftSettings();
    const currentUser = this.authService.currentUser();
    const auction = this.socketService.currentAuction();
    const countdown = this.socketService.countdownValue();

    if (countdown !== null) return 'var(--base-error)';

    if (!settings) return 'var(--base-primary)';

    switch (settings.status) {
      case 'not_started':
      case 'ended':
        return 'var(--base-primary)';

      case 'paused':
        return 'var(--base-warning)';

      case 'in_progress':
        if (auction) {
          // Active bidding
          if (auction.highBidder.id === currentUser?.id) {
            return 'var(--base-success)';
          }
          return 'var(--base-blue)';
        }
        if (settings.lastAuctionResult) {
          // Auction just finished
          if (settings.lastAuctionResult.winner.id === currentUser?.id) {
            return 'var(--base-success)';
          }
          return 'var(--base-error)';
        }
        // Nomination phase
        if (settings.currentNominatorUserId === currentUser?.id) {
          return 'var(--base-blue)';
        }
        return 'var(--base-primary)';

      default:
        return 'var(--base-primary)';
    }
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private leagueService: LeagueService,
    public socketService: SocketService,
    private authService: AuthService
  ) {}

  async ngOnInit(): Promise<void> {
    this.leagueHash = this.route.snapshot.paramMap.get('leagueIdHash');

    if (!this.leagueHash) {
      this.error.set('Invalid league ID');
      this.isLoading.set(false);
      return;
    }

    await this.loadLeague();
    this.initializeSocket();
  }

  ngOnDestroy(): void {
    const league = this.league();
    if (league?.id) {
      this.socketService.leaveDraft(league.id);
    }
    this.socketService.disconnect();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private initializeSocket(): void {
    this.socketService.connect();

    // Subscribe to socket events
    this.subscriptions.push(
      this.socketService.onUserJoined.subscribe(({ user }) => {
        this.addSystemMessage(`${user.name} joined the draft`);
      }),
      this.socketService.onUserLeft.subscribe(({ user }) => {
        this.addSystemMessage(`${user.name} left the draft`);
      }),
      this.socketService.onNomination.subscribe((auction) => {
        let message = `${auction.nominatedBy.name} nominated ${auction.player.first_name} ${auction.player.last_name} for $${auction.currentBid}`;
        if (auction.nominatedByCommissioner && auction.commissionerName) {
          message += ` (by ${auction.commissionerName})`;
        }
        this.addSystemMessage(message);
      }),
      this.socketService.onBidUpdate.subscribe(({ bidder, amount, player }) => {
        this.addSystemMessage(
          `${bidder.name} bid $${amount} on ${player.first_name} ${player.last_name}`
        );
      }),
      this.socketService.onAuctionWon.subscribe(({ winner, player, amount }) => {
        this.addSystemMessage(
          `${winner.name} acquired ${player.first_name} ${player.last_name} for $${amount}!`
        );
        this.removeFromWatchlist(player.id);
      }),
      this.socketService.onDraftStarted.subscribe(() => {
        this.addSystemMessage('Draft has started!');
      }),
      this.socketService.onDraftPaused.subscribe(() => {
        this.addSystemMessage('Draft has been paused');
      }),
      this.socketService.onDraftResumed.subscribe(() => {
        this.addSystemMessage('Draft has resumed');
      }),
      this.socketService.onPlayerUndrafted.subscribe(({ player, team }) => {
        this.addSystemMessage(
          `${player.first_name} ${player.last_name} was removed from ${team.name}`
        );
      }),
      this.socketService.onError.subscribe(({ message }) => {
        this.addSystemMessage(`Error: ${message}`);
      })
    );

    // Join draft room once connected
    const checkConnection = setInterval(() => {
      const league = this.league();
      if (this.socketService.isConnected() && league?.id) {
        this.socketService.joinDraft(league.id);
        clearInterval(checkConnection);
      }
    }, 100);

    // Clear interval after 10 seconds to prevent infinite loop
    setTimeout(() => clearInterval(checkConnection), 10000);
  }

  private addSystemMessage(message: string): void {
    const systemMessage: SystemMessage = {
      message,
      timestamp: new Date().toISOString()
    };
    this.systemMessages.update(messages => [...messages.slice(-19), systemMessage]);
  }

  async loadLeague(): Promise<void> {
    if (!this.leagueHash) return;

    this.isLoading.set(true);
    this.error.set(null);

    try {
      const data = await this.leagueService.getLeagueByHash(this.leagueHash);
      this.league.set(data);

      // Check if user is commissioner
      if (data.id) {
        await this.checkCommissionerStatus(data.id);
      }
      this.loadWatchlist();
      await this.loadAllPlayers();
    } catch (err) {
      this.error.set('Failed to load league');
      console.error('Error loading league:', err);
    } finally {
      this.isLoading.set(false);
    }
  }

  private loadWatchlist(): void {
    const league = this.league();
    const currentUser = this.authService.currentUser();
    if (!league?.id || !currentUser?.id) return;

    const key = `watchlist_${currentUser.id}_${league.id}`;
    try {
      const stored = localStorage.getItem(key);
      const ids: number[] = stored ? JSON.parse(stored) : [];
      this.watchlistPlayerIds.set(new Set(ids));
    } catch {
      this.watchlistPlayerIds.set(new Set());
    }
  }

  toggleWatchlist(event: { playerId: number, add: boolean }): void {
    const league = this.league();
    const currentUser = this.authService.currentUser();
    if (!league?.id || !currentUser?.id) return;

    const key = `watchlist_${currentUser.id}_${league.id}`;
    this.watchlistPlayerIds.update(ids => {
      const next = new Set(ids);
      event.add ? next.add(event.playerId) : next.delete(event.playerId);
      try {
        localStorage.setItem(key, JSON.stringify([...next]));
      } catch { /* storage full or unavailable */ }
      return next;
    });
  }

  private removeFromWatchlist(playerId: number): void {
    const league = this.league();
    const currentUser = this.authService.currentUser();
    if (!league?.id || !currentUser?.id) return;

    this.watchlistPlayerIds.update(ids => {
      if (!ids.has(playerId)) return ids;
      const next = new Set(ids);
      next.delete(playerId);
      try {
        const key = `watchlist_${currentUser.id}_${league.id}`;
        localStorage.setItem(key, JSON.stringify([...next]));
      } catch { /* storage full or unavailable */ }
      return next;
    });
  }

  private async checkCommissionerStatus(leagueId: number): Promise<void> {
    try {
      const response = await fetch(`/api/leagues/${leagueId}/user-role`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        this.isCommissioner.set(data.isCommissioner);
      }
    } catch (err) {
      console.error('Error checking commissioner status:', err);
    }
  }

  readonly positionColors: Record<string, string> = {
    'QB':  'oklch(62% 0.14 25)',
    'RB':  'oklch(62% 0.12 145)',
    'WR':  'oklch(58% 0.13 250)',
    'TE':  'oklch(66% 0.13 40)',
    'K':   'oklch(62% 0.14 305)',
    'PK':  'oklch(62% 0.14 305)',
    'DEF': 'oklch(55% 0.08 80)',
    'DST': 'oklch(55% 0.08 80)',
  };

  readonly teamColorPalette = [
    'oklch(62% 0.12 145)',
    'oklch(62% 0.14 305)',
    'oklch(66% 0.13 40)',
    'oklch(58% 0.13 250)',
    'oklch(68% 0.12 85)',
  ];

  get rosterSize(): number {
    return this.league()?.roster_size ?? 16;
  }

  teamColor(index: number): string {
    return this.teamColorPalette[index % this.teamColorPalette.length];
  }

  isUserOnline(userId: number): boolean {
    return this.socketService.connectedUsers().some(u => u.id === userId);
  }

  teamFilled(team: TeamInfo): number {
    return (this.socketService.draftSettings()?.draftHistory || [])
      .filter(e => e.team_id === team.team_id).length;
  }

  teamRemaining(team: TeamInfo): number {
    const budget = this.socketService.draftSettings()?.teamBudget ?? this.league()?.team_budget ?? 200;
    const spent = (this.socketService.draftSettings()?.draftHistory || [])
      .filter(e => e.team_id === team.team_id)
      .reduce((s, e) => s + e.cost, 0);
    return budget - spent;
  }

  allRosterSlots(): number[] {
    return Array.from({ length: this.rosterSize }, (_, i) => i);
  }

  slotPositionColor(team: TeamInfo, slot: number): string {
    const entry = this.slotEntry(team, slot);
    if (!entry) return '';
    return this.positionColors[entry.position] ?? '';
  }

  slotEntry(team: TeamInfo, slot: number): DraftHistoryEntry | null {
    const history = (this.socketService.draftSettings()?.draftHistory || [])
      .filter(e => e.team_id === team.team_id);
    return history[slot] ?? null;
  }

  onSlotClick(team: TeamInfo, slot: number): void {
    const entry = this.slotEntry(team, slot);
    if (entry) this.selectedHandCard.set(entry);
  }

  emptySlots(filledCount: number): number[] {
    return Array.from({ length: Math.max(0, this.rosterSize - filledCount) }, (_, i) => i);
  }

  watchlistCardData(p: Player): PlayerCardData {
    return { firstName: p.first_name, lastName: p.last_name, position: p.position, team: p.team, photo: p.photo };
  }

  handCardData(e: DraftHistoryEntry): PlayerCardData {
    return { firstName: e.first_name, lastName: e.last_name, position: e.position, team: e.pro_team, photo: e.photo, cost: e.cost };
  }

  get canNominate(): boolean {
    return this.draftStage?.stageView() === 'choose_player';
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.selectedHandCard()) this.selectedHandCard.set(null);
  }

  onHandCardClick(entry: DraftHistoryEntry): void {
    this.selectedHandCard.set(entry);
  }

  closeHandCardModal(): void {
    this.selectedHandCard.set(null);
  }

  onWatchlistPlayerClick(player: any): void {
    if (this.canNominate || this.draftStage?.showCommissionerOverride()) {
      this.draftStage.selectPlayer(player as Player);
    }
  }

  private async loadAllPlayers(): Promise<void> {
    const league = this.league();
    if (!league?.id) return;
    try {
      const res = await fetch(`/api/leagues/${league.id}/cheat-sheet`, { credentials: 'include' });
      if (res.ok) this.allPlayers.set(await res.json());
    } catch { /* non-critical */ }
  }

  goBack(): void {
    this.router.navigate(['/leagues']);
  }

  trackByUserId(index: number, user: ConnectedUser): number {
    return user.id;
  }

  toggleChatDrawer(): void {
    const opening = !this.isChatDrawerOpen();
    this.isChatDrawerOpen.set(opening);
    if (opening) {
      this.unreadChatCount.set(0);
    }
  }

  closeChatDrawer(): void {
    this.isChatDrawerOpen.set(false);
  }

  toggleCheatSheet(): void {
    this.isCheatSheetOpen.update(isOpen => !isOpen);
  }

  closeCheatSheet(): void {
    this.isCheatSheetOpen.set(false);
  }

  toggleTape(): void {
    this.isTapeCollapsed.update(v => !v);
  }

  onCheatSheetPlayerSelected(player: Player): void {
    this.closeCheatSheet();
    if (this.draftStage?.stageView() === 'choose_player' || this.draftStage?.showCommissionerOverride()) {
      this.draftStage.selectPlayer(player);
    }
  }
}
